export const REFUND_ACTION = {
  accept: "accept",
  review: "review",
  assign: "assign",
  decline: "decline",
  autoQuote: "autoQuote",
  quote: "quote",
  approveQuote: "approveQuote",
  submit: "submit",
  retryAuto: "retryAuto",
  reimburse: "reimburse",
  checkStatus: "checkStatus",
  remark: "remark",
  messages: "messages",
  supplierResponse: "supplierResponse",
};

export function hasRefundSupplierResponse(data) {
  const response = data?.supplierResponse;
  if (response == null) return false;
  if (typeof response === "object") {
    return Object.keys(response).length > 0;
  }
  return true;
}

/** Legacy rejectReason code; airline declines now use POST declineBySupplier. */
export const REFUND_AIRLINE_DECLINED_REASON = "AIRLINE_DECLINED";

function normalizeRefundStatus(status) {
  return String(status || "")
    .replace(/[_\s-]/g, "")
    .toLowerCase();
}

const TRAVELLER_MAP = {
  ADT: "ADT",
  Adult: "ADT",
  CHD: "CHD",
  CNN: "CHD",
  Child: "CHD",
  INS: "INF",
  INF: "INF",
  Infant: "INF",
};

export function normalizeRefundTravelerType(type) {
  return TRAVELLER_MAP[type] || type || "ADT";
}

export function isRefundQuoteExpired(data) {
  if (data?.confirmed) return false;
  const raw = data?.requestExpirationTime;
  if (!raw) return false;
  const expiry = new Date(raw);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() < Date.now();
}

export function hasRefundQuote(data) {
  return Boolean(
    data?.priceReceived ||
      data?.isQuoted ||
      isRefundQuotedStatus(data?.status) ||
      (Array.isArray(data?.passengerPrice) && data.passengerPrice.length),
  );
}

export function isManualReviewStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "manualreviewrequired" ||
    normalized === "autorefundfailed"
  );
}

export function isManualRefundProcessingStatus(status) {
  return normalizeRefundStatus(status) === "manualrefundprocessing";
}

export function isRefundQuotedStatus(status) {
  return normalizeRefundStatus(status) === "refundquoted";
}

export function isRefundAcceptedByAgentStatus(status) {
  return normalizeRefundStatus(status) === "refundaccepted";
}

export function isSupplierProcessingStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "supplierprocessing" ||
    normalized === "submittedtosupplier" ||
    normalized === "refundsubmitting" ||
    normalized === "autorefundsubmission"
  );
}

export function isRefundCompletedStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "refundcompleted" ||
    normalized === "success" ||
    normalized === "closed"
  );
}

export function isRefundValidationFailedStatus(status) {
  return normalizeRefundStatus(status) === "refundvalidationfailed";
}

export function isAutoRefundProcessingStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "autorefundprocessing" ||
    normalized === "refundrequestsubmitted"
  );
}

export function isRefundSendFailedStatus(status) {
  return normalizeRefundStatus(status) === "autorefundsubmissionfailed";
}

export function isRefundSuccessfulStatus(status) {
  return normalizeRefundStatus(status) === "refundsuccessful";
}

export function isRefundSettlementPendingStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "settlementpending" || normalized === "refundcredited"
  );
}

export function isRefundSettlementFailedStatus(status) {
  return normalizeRefundStatus(status) === "settlementfailed";
}

export function isRefundSupplierFailedStatus(status) {
  const normalized = normalizeRefundStatus(status);
  return (
    normalized === "refundfailedbysupplier" ||
    normalized === "refunddeclinedbysupplier" ||
    normalized === "failed"
  );
}

export function isRefundQuoteExpiredStatus(status) {
  return normalizeRefundStatus(status) === "refundquoteexpired";
}

export function isRefundDeclinedByAgentStatus(status) {
  return normalizeRefundStatus(status) === "refunddeclinedbyagent";
}

export function isRefundAcceptedStatus(status) {
  return status === "Approved" || isManualRefundProcessingStatus(status);
}

export function canSubmitRefundToAirline(data) {
  const stage = getRefundAdminStage(data);
  return ["needs_airline_submit", "send_failed"].includes(stage);
}

export function canEnterRefundQuote(data) {
  const stage = getRefundAdminStage(data);
  if (["needs_quote", "awaiting_agent", "quote_expired"].includes(stage)) {
    return true;
  }
  return stage === "auto_processing" && isRefundAcceptedStatus(data);
}

/** Admin accepts quote for agent — only live RefundQuoted + immediateRefund. */
export function canApproveImmediateRefundQuote(data) {
  if (!data?.immediateRefund) return false;
  if (data?.confirmed) return false;
  if (isRefundQuoteExpired(data) || isRefundQuoteExpiredStatus(data?.status)) {
    return false;
  }
  return isRefundQuotedStatus(data?.status);
}

/**
 * Soft or hard expired quote — admin should enter a new one.
 * Soft expiry (requestExpirationTime) only applies while status is RefundQuoted.
 */
export function isRefundQuoteExpiredForDisplay(data) {
  if (!data || data?.confirmed) return false;
  if (isRefundQuoteExpiredStatus(data?.status)) return true;
  return isRefundQuotedStatus(data?.status) && isRefundQuoteExpired(data);
}

export function getRefundApproveQuoteActionLabel() {
  return "Approve quote";
}

export function canCheckRefundSupplierStatus(data) {
  const stage = getRefundAdminStage(data);
  return [
    "awaiting_supplier",
    "needs_reimburse",
    "settlement_failed",
    "supplier_failed",
    "send_failed",
  ].includes(stage);
}

export function canGetAirlinePrice(data) {
  const stage = getRefundAdminStage(data);
  if (data?.confirmed || data?.priceReceived) return false;
  // Only after Accept (needs_quote / auto / expired) — not on ManualReviewRequired
  return ["needs_quote", "auto_processing", "quote_expired"].includes(stage);
}

export function canCreditRefundWallet(data) {
  const stage = getRefundAdminStage(data);
  return [
    "needs_reimburse",
    "settlement_failed",
    "awaiting_supplier",
    "send_failed",
    "supplier_failed",
  ].includes(stage);
}

export function isRefundWalletCreditStage(data) {
  const stage = getRefundAdminStage(data);
  return stage === "needs_reimburse" || stage === "settlement_failed";
}

export function isRefundCloseRequestStage(data) {
  const stage = getRefundAdminStage(data);
  return stage === "supplier_failed" || stage === "validation_failed";
}

export function canDeclineRefundRequest(data) {
  const stage = getRefundAdminStage(data);
  return [
    "needs_review",
    "needs_manual_review",
    "needs_quote",
    "awaiting_agent",
    "quote_expired",
    "needs_airline_submit",
    "awaiting_supplier",
    "send_failed",
    "auto_processing",
    "supplier_failed",
    "validation_failed",
  ].includes(stage);
}

/**
 * Quote already accepted by agent (or admin for immediate) — decline must
 * ask airline vs team (different endpoints).
 */
export function isRefundPostQuoteAcceptedStage(data) {
  const stage = getRefundAdminStage(data);
  return [
    "needs_airline_submit",
    "awaiting_supplier",
    "send_failed",
    "supplier_failed",
  ].includes(stage);
}

/**
 * Statuses where POST declineBySupplier is allowed (backend).
 * Team decline remains PATCH deny.
 */
export function canRecordRefundSupplierDecline(data) {
  const status = normalizeRefundStatus(data?.status);
  return [
    "manualreviewrequired",
    "manualrefundprocessing",
    "refundaccepted",
    "submittedtosupplier",
    "supplierprocessing",
    "reconciliationrequired",
    "autorefundfailed",
  ].includes(status);
}

/** Show airline vs team choice when supplier decline is allowed (or post-accept). */
export function shouldChooseRefundDeclineSource(data) {
  if (isRefundCloseRequestStage(data)) return false;
  return (
    canRecordRefundSupplierDecline(data) ||
    isRefundPostQuoteAcceptedStage(data)
  );
}

export function canAssignRefundRequest(data) {
  const stage = getRefundAdminStage(data);
  return !["completed", "declined"].includes(stage);
}

export function canAddRefundRemark(data) {
  return canAssignRefundRequest(data);
}

export function isRefundAutoProcessingMethod(data) {
  return String(data?.processingMethod || "").toLowerCase() === "auto";
}

/** Retry PATCH /refund/confirm/:id — Auto refunds stuck on ManualReviewRequired. */
export function canRetryAutoRefund(data) {
  if (!data || !isRefundAutoProcessingMethod(data)) return false;
  return normalizeRefundStatus(data?.status) === "manualreviewrequired";
}

export function getRefundRetryAutoActionLabel() {
  return "Retry auto submission";
}

export function getRefundRetryAutoConfirmLabel() {
  return "Yes, retry automatic refund";
}

export function getRefundRetryAutoLoadingTitle() {
  return "Retrying automatic refund submission";
}

export function getRefundRetryAutoWarningCopy() {
  return {
    phase: "warn",
    kind: "retry_auto_warn",
    title: "Retry automatic refund submission?",
    text: "This asks Intraverse to submit the refund to the airline again automatically (PATCH confirm). Use this when the last auto attempt failed or stalled. It does not refund the ticket by hand on the airline site, and it does not pay the agent. Stay on this screen while it runs. A timeout here is not an airline decline.",
  };
}

export function getRefundRetryAutoCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "retry_autoed",
      title: "Automatic refund send started again",
      text: "Intraverse is retrying the automatic refund. Wait for the status to update. Do not credit the agent wallet yet. Do not submit a second refund on the airline site unless auto fails again.",
    };
  }
  return {
    phase: "error",
    kind: "retry_auto_failed",
    title: "Automatic refund retry failed",
    text: `${msg || "The request failed or timed out"}. That is not an airline decline. You can try auto again.`,
  };
}

export function getRefundQuoteActionLabel(data) {
  const stage = getRefundAdminStage(data);
  if (stage === "quote_expired") return "Enter new quote from airline";
  if (hasRefundQuote(data) || isRefundQuotedStatus(data?.status)) {
    return "Edit quote";
  }
  return "Enter quote from airline";
}

export function getRefundSubmitActionLabel() {
  return "I submitted it on the airline site";
}

export function getRefundReimburseActionLabel(data) {
  return isRefundWalletCreditStage(data)
    ? "Credit agent wallet"
    : "Already refunded on airline — finish here";
}

export function getRefundDeclineActionLabel(data) {
  return isRefundCloseRequestStage(data)
    ? "Close this request"
    : "Decline request";
}

export function getRefundAssignActionLabel(data) {
  return data?.tasks?.length ? "Reassign" : "Assign to teammate";
}

function refundStatusNote(data) {
  return String(
    data?.manualInterventionReason ||
      data?.lastError ||
      data?.errorMessage ||
      data?.rejectReason ||
      "",
  );
}

/** Quote path: get numbers from airline first. Do not submit the refund yet. */
const REFUND_QUOTE_PATH_STEPS = [
  "On this screen: check ticket number, passengers, and reason.",
  "Open the airline or supplier site and find out how much they will refund (refundable value and charges). You need those real numbers — do not invent them.",
  "Do not submit / process the refund on the airline site yet. Only look up the price.",
  "Accept this request if it should go ahead (if not already accepted).",
  'Then either "Get supplier price" (retry), or "Enter quote from airline". Enter airline Penalty / Supplier Fee (deducted) and Base / Tax Return (what airline pays back). Markup on Base/Tax Return reduces what the agent gets (you keep the difference). Base+Tax cannot exceed that pax type unit ticket price (adult/child/infant total ÷ count). Service fee comes from fee config.',
  "Send the quote to the agent. Do not pay the agent yet. Check airline vs adjusted deductions and payable.",
];

/** Submit path: agent already accepted. Submit refund on airline site. */
const REFUND_AIRLINE_SUBMIT_STEPS = [
  "Open the airline or supplier website (portal / GDS).",
  "Find this ticket or PNR.",
  "Submit the refund there yourself. That is the real refund — Intraverse has not refunded it yet.",
  "Come back here.",
  'Click "I submitted it on the airline site". That only tells Intraverse you sent it. It does not pay the agent.',
  'Use "Already refunded on airline — finish here" only if the airline site already shows the ticket as refunded. That finishes the request and pays the agent.',
];

const REFUND_AIRLINE_CHECK_ONLY_STEPS = [
  "Do not submit a new refund on the airline site.",
  "You may open the airline site only to check the ticket / refund status.",
  "Wait for the airline answer in Intraverse, or use Check supplier status.",
  'If this is stuck and the airline already shows refunded, use "Already refunded on airline — finish here".',
  "Do not credit the agent wallet until the airline refund has succeeded.",
];

export function getRefundReviewTrigger(data) {
  const code = normalizeRefundStatus(data?.status);
  if (code === "refundvalidationfailed") return "validation_failed";
  if (code === "autorefundsubmissionfailed") return "send_failed";
  if (code === "autorefundfailed") return "auto_quote_failed";
  if (
    code === "manualreviewrequired" ||
    code === "autorefundprocessing" ||
    code === "pending" ||
    code === "pendingrefund"
  ) {
    if (Boolean(data?.confirmed) || isRefundAcceptedByAgentStatus(data?.status)) {
      return "send_failed";
    }
    // ManualReviewRequired / AutoRefundProcessing = eligibility Accept/Decline first
    return "needs_review";
  }
  return "needs_review";
}

export function getRefundStatusLabelForCode(status) {
  const overlay = getRefundStatusOverlay({ status }, { stage: "" });
  if (overlay?.statusLabel) return overlay.statusLabel;
  const fallback = {
    pending: "Ops must review this refund",
    manualreviewrequired: "Ops must review this refund",
    autorefundprocessing: "Ops must review this refund",
    failed: "Airline declined refund",
    denied: "Declined",
    rejected: "Declined",
  };
  return fallback[normalizeRefundStatus(status)] || null;
}

export function getRefundReviewGuidance(data) {
  const trigger = getRefundReviewTrigger(data);
  const note = refundStatusNote(data);
  const statusDisplay =
    getRefundStatusLabelForCode(data?.status) || data?.status || "Unknown";

  const shared = {
    trigger,
    note,
    statusDisplay,
    whyTitle: "Why you're here",
    showAccept: false,
    showDecline: true,
    showQuote: false,
    showSubmit: false,
    showReimburse: false,
    acceptLabel: "Accept — then create a quote",
    declineLabel: "Decline",
    quoteLabel: getRefundQuoteActionLabel(data),
    submitLabel: getRefundSubmitActionLabel(),
    reimburseLabel: getRefundReimburseActionLabel(data),
  };

  const copies = {
    validation_failed: {
      title: "This refund could not be started",
      why: "A system check failed before anything was sent. Ticket missing, wrong agent, already refunded/voided, conflicting request, or similar. No money moved.",
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "The agent can submit again if the ticket is still eligible.",
      ],
      questions: ["Which check failed?"],
      declineLabel: "Close this request",
    },
    auto_quote_failed: {
      title: "Automatic refund price failed",
      why: "Intraverse tried to get a refund price automatically and hit a technical or calculation problem. That is not an airline decline. There is no quote yet. Do not submit a refund to the airline and do not pay the agent.",
      steps: REFUND_QUOTE_PATH_STEPS,
      questions: [
        "Did our price call fail, or did the airline actually refuse a refund?",
        "A timeout is not an airline no.",
      ],
      showAccept: !isRefundAcceptedStatus(data?.status),
      showQuote: canEnterRefundQuote(data),
    },
    send_failed: {
      title: "Automatic refund send failed",
      why: "The agent already accepted the quote (or send failed after accept). Intraverse could not submit the refund to the airline. That is a technical problem, not an airline decline. The ticket is not refunded yet — you must submit the refund on the airline site.",
      steps: REFUND_AIRLINE_SUBMIT_STEPS,
      questions: [
        "Did our send fail, or did the airline actually refuse?",
        "A timeout is not an airline no.",
      ],
      showAccept: false,
      showSubmit: true,
      showReimburse: true,
      declineLabel: "Decline request",
    },
    needs_review: {
      title: "Ops must review this refund",
      why: "A person must look at this. The supplier may not support automatic pricing, or Ops must confirm the ticket can be refunded. Accepting is not a refund yet — next you create a quote. Do not submit to the airline yet.",
      steps: [
        "On this screen: check ticket number, passengers, and reason.",
        "Open the airline site only to CHECK eligibility — do not submit a refund yet.",
        "If it can go ahead: Accept. Then get or type a quote.",
        "If it cannot: Decline and write a plain-language reason.",
      ],
      questions: [
        "Is this the right issued ticket?",
        "Is a refund allowed for this fare / use?",
        "Should Intraverse take this refund, or decline it?",
      ],
      showAccept: true,
    },
  };

  return { ...shared, ...(copies[trigger] || copies.needs_review) };
}

function getRefundStatusOverlay(data, { stage }) {
  const code = normalizeRefundStatus(data?.status);
  const note = refundStatusNote(data);
  const noteLine = note ? ` ${note}` : "";
  const reason = data?.rejectReason || data?.reason || "";

  const overlays = {
    refundrequestsubmitted: {
      statusLabel: "Request received — getting price",
      title: "Refund request received",
      description:
        "Validation passed. The system may get a supplier price automatically, or Ops may take over. Do not credit the agent wallet yet.",
      steps: [
        "Wait for a price, or take over if this sits too long.",
        "Do not submit a refund to the airline yet.",
        "Do not pay the agent yet.",
      ],
      tone: "wait",
    },
    refundvalidationfailed: {
      statusLabel: "Could not start",
      title: "This refund could not be started",
      description: `A system check failed before anything was sent.${noteLine} No money moved. Close this request.`,
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "Do not submit a refund on the airline site for this failed request.",
      ],
      tone: "danger",
    },
    autorefundprocessing: {
      statusLabel: "Ops must review this refund",
      title: "Ops must review this refund",
      description:
        "Automatic refund processing is in progress or waiting on Ops. Treat this like Manual Review Required: check the ticket, then Accept or Decline. After Accept you create a quote. Do not submit on the airline site yet.",
      steps: [
        "Open Review.",
        "Check ticket, passengers, and reason.",
        "Open the airline site only to CHECK eligibility — do not submit a refund yet.",
        "Accept or Decline.",
      ],
      tone: "action",
    },
    autorefundfailed: {
      statusLabel: "Price failed — Ops must quote",
      title: "Automatic refund price failed",
      description: `That is a technical or calculation problem, not an airline decline.${noteLine} Review, then get or type a quote. Do not submit the refund to the airline yet. Do not pay the agent.`,
      steps: REFUND_QUOTE_PATH_STEPS,
      tone: "action",
    },
    manualreviewrequired:
      stage === "needs_airline_submit" || stage === "send_failed"
        ? {
            statusLabel: "Submit refund on airline site",
            title: "Submit this refund on the airline site",
            description: `The quote was accepted (or auto send failed).${noteLine} Intraverse has not refunded the ticket. Submit the refund yourself on the airline site, then come back.`,
            steps: REFUND_AIRLINE_SUBMIT_STEPS,
            tone: "action",
          }
        : stage === "awaiting_agent" || stage === "quote_expired"
          ? {
              statusLabel:
                stage === "quote_expired"
                  ? "Quote expired — quote again"
                  : "Waiting for agent to accept quote",
              title:
                stage === "quote_expired"
                  ? "Quote expired — enter a new one"
                  : "Waiting for the agent",
              description:
                stage === "quote_expired"
                  ? "Enter a new quote. Do not submit to the airline on the expired quote. Do not pay the agent."
                  : "The quote is with the agent. Do not submit to the airline until they accept. Do not pay the agent yet.",
              steps:
                stage === "quote_expired"
                  ? [
                      "Enter a new quote with a fresh expiry.",
                      "Do not submit this refund to the airline yet.",
                      "Do not credit the agent wallet.",
                    ]
                  : [
                      "Wait for the agent to accept or decline.",
                      "You may edit the quote if figures are wrong.",
                      "Do not submit to the airline until they accept.",
                    ],
              tone: stage === "quote_expired" ? "danger" : "wait",
            }
          : {
              statusLabel: "Ops must review this refund",
              title: "Ops must review this refund",
              description:
                "A person must look at this. Do not submit a refund on the airline site yet. Check the ticket, then Accept or Decline. After Accept, create a quote for the agent.",
              steps: [
                "On this screen: check ticket, passengers, and reason.",
                "Open the airline site only to CHECK eligibility — do not submit a refund yet.",
                "If it can go ahead: Accept, then create a quote.",
                "If it cannot: Decline and write why.",
              ],
              tone: "action",
            },
    pending: {
      statusLabel: "Ops must review this refund",
      title: "Ops must review this refund",
      description:
        "An agent submitted a refund. Do not submit on the airline site yet. Check the facts, then Accept or Decline.",
      steps: [
        "Open Review.",
        "Check ticket, passengers, and reason.",
        "Accept or Decline. Quote comes after Accept.",
      ],
      tone: "action",
    },
    manualrefundprocessing:
      stage === "needs_airline_submit" || stage === "send_failed"
        ? {
            statusLabel: "Submit refund on airline site",
            title: "Submit this refund on the airline site now",
            description:
              "The agent already accepted the quote. Intraverse has not refunded the ticket. Open the airline site, submit the refund there, then come back.",
            steps: REFUND_AIRLINE_SUBMIT_STEPS,
            tone: "action",
          }
        : {
            statusLabel: "Get price — then type the quote",
            title: "Your job now: get the refund price, then type the quote",
            description:
              "Do not submit the refund on the airline site yet. Do not pay the agent. First get the airline/supplier refund numbers, then enter the quote in Intraverse so the agent can accept.",
            steps: [
              "Open the airline or supplier site.",
              "Find out how much the airline will refund (refundable value and charges). You need those real numbers — do not invent them.",
              "Do not click the airline’s final “refund / process refund” yet — the agent has not accepted a quote.",
              "Come back to Intraverse.",
              'Click "Get supplier price" to retry, or "Enter quote from airline" and type those numbers in.',
              "Send the quote to the agent. Wait for them to accept. Only after that do you submit the refund on the airline site.",
            ],
            tone: "action",
          },
    approved: {
      statusLabel: "Get price — then enter quote",
      title: "Your job now: get the airline refund price, then enter the quote",
      description:
        "This request is accepted. First learn how much the airline will refund. Then enter that as a quote. Do not submit the refund on the airline site yet. Do not pay the agent.",
      steps: [
        "Open the airline site and get the refund numbers (do not process the refund yet).",
        'Retry "Get supplier price", or "Enter quote from airline" with those numbers.',
        "Send the quote to the agent.",
        "Do not submit the refund to the airline until the agent accepts.",
      ],
      tone: "action",
    },
    refundquoted:
      stage === "quote_expired"
        ? {
            statusLabel: "Quote expired — quote again",
            title: "Quote expired — enter a new one",
            description:
              "The agent did not confirm in time. Enter a new quote. Do not submit to the airline on the old quote. Do not pay the agent.",
            steps: [
              "Enter a new quote with a fresh expiry.",
              "Do not submit this refund to the airline yet.",
              "Do not credit the agent wallet.",
            ],
            tone: "danger",
          }
        : data?.immediateRefund
          ? {
              statusLabel: "Approve quote (immediate refund)",
              title: "Immediate refund — approve the quote for the agent",
              description:
                "The agent chose immediate refund. Review the quote carefully, then Approve quote. That accepts on their behalf. Do not credit the wallet yet.",
              steps: [
                "Open Approve quote and check breakdown, agent payable, and your profit.",
                "Approve only if the figures are correct.",
                "Then submit on the airline site. Do not credit the wallet yet.",
              ],
              tone: "action",
            }
          : {
              statusLabel: "Waiting for agent to accept quote",
              title: "Waiting for the agent to accept the quote",
              description:
                "The quote is with the agent. Do not submit the refund to the airline yet. Do not credit the agent wallet.",
              steps: [
                "Wait for accept or decline.",
                "Edit the quote only if figures need to change.",
                "Do not submit on the airline site yet.",
              ],
              tone: "wait",
            },
    refundquoteexpired: {
      statusLabel: "Quote expired — quote again",
      title: "Quote expired — enter a new one",
      description:
        "The agent did not confirm in time. Enter a new quote. Do not submit to the airline on the old quote. Do not pay the agent.",
      steps: [
        "Enter a new quote with a fresh expiry.",
        "Do not submit this refund to the airline yet.",
        "Do not credit the agent wallet.",
      ],
      tone: "danger",
    },
    refunddeclinedbyagent: {
      statusLabel: "Agent declined",
      title: "Agent declined this refund",
      description:
        "The agent declined the quote. Do not submit to the airline. Do not credit the wallet.",
      steps: [],
      tone: "danger",
    },
    refundaccepted: {
      statusLabel: "Submit refund on airline site",
      title: "Agent accepted. Submit the refund on the airline site",
      description:
        "The agent accepted the commercial terms. Intraverse has not refunded the ticket yet. Submit the refund on the airline or supplier site, then come back.",
      steps: REFUND_AIRLINE_SUBMIT_STEPS,
      tone: "action",
    },
    refundsubmitting: {
      statusLabel: "Sending to airline",
      title: "Sending this refund to the airline",
      description:
        "The computer is submitting the refund instruction. Stay on this request. A timeout is not an airline decline.",
      steps: [
        "Wait a short time.",
        "If stuck: take over and submit on the airline site yourself.",
      ],
      tone: "wait",
    },
    autorefundsubmission: {
      statusLabel: "Sending to airline",
      title: "Automatic refund submission in progress",
      description:
        "Intraverse is sending the refund to the supplier. A timeout is not an airline decline.",
      steps: [
        "Wait a short time.",
        "If stuck: submit on the airline site yourself, then come back.",
      ],
      tone: "wait",
    },
    autorefundsubmissionfailed: {
      statusLabel: "Send failed — submit on airline site",
      title: "Automatic send failed. Submit on the airline site",
      description: `That is a technical problem, not an airline decline.${noteLine} The ticket is not refunded yet. Submit the refund yourself on the airline site.`,
      steps: REFUND_AIRLINE_SUBMIT_STEPS,
      tone: "action",
    },
    submittedtosupplier: {
      statusLabel: "Waiting on airline",
      title: "Waiting on the airline — do not submit again",
      description:
        "The refund has already been submitted. Do not submit again. Wait for the airline answer, or check status only. Do not pay the agent yet.",
      steps: REFUND_AIRLINE_CHECK_ONLY_STEPS,
      tone: "wait",
    },
    supplierprocessing: {
      statusLabel: "Waiting on airline",
      title: "Airline is still working — do not submit again",
      description:
        "The refund was already sent. Do not submit a new refund. Wait or check status only. A timeout is not an airline decline.",
      steps: REFUND_AIRLINE_CHECK_ONLY_STEPS,
      tone: "wait",
    },
    refundsuccessful: {
      statusLabel: "Airline refunded — credit wallet",
      title: "The airline refunded this ticket. Credit the agent wallet",
      description:
        "Supplier refund succeeded. Credit the net refund to the agent wallet. If money fails, the airline refund stays successful — fix money separately.",
      steps: [
        "Do not submit another refund on the airline site.",
        "Credit the agent wallet.",
        "If money fails, keep this as a successful airline refund.",
      ],
      tone: "action",
    },
    settlementpending: {
      statusLabel: "Settling money",
      title: "Settlement is running",
      description:
        "Crediting the agent wallet. Do not change a successful airline refund back to failed if money fails.",
      steps: [
        "Wait for wallet credit, or retry Credit agent wallet.",
        "Do not void the airline refund outcome.",
      ],
      tone: "action",
    },
    refundcredited: {
      statusLabel: "Wallet credited",
      title: "Agent wallet has been credited",
      description:
        "The net refund was credited. This should move to completed.",
      steps: [],
      tone: "action",
    },
    settlementfailed: {
      statusLabel: "Airline done — money failed",
      title: "Wallet settlement failed",
      description:
        "The airline refund stays successful. Retry crediting the agent wallet. Do not mark this refund as failed.",
      steps: [
        "Do not change the airline outcome.",
        "Retry Credit agent wallet.",
      ],
      tone: "danger",
    },
    refunddeclinedbysupplier: {
      statusLabel: "Airline declined refund",
      title: "The airline declined this refund",
      description: reason
        ? `The airline said no. Reason: ${reason}. This is not a technical timeout. Do not credit the agent wallet.`
        : "The airline declined this refund. This is not a technical timeout. Do not credit the agent wallet.",
      steps: [
        "Read the airline reason.",
        "Close this request if the ticket stays issued.",
        "Do not pay the agent unless you later confirm the airline did refund it.",
      ],
      tone: "danger",
    },
    refundfailedbysupplier: {
      statusLabel: "Airline could not refund it",
      title: "The airline could not refund this ticket",
      description: reason
        ? `The airline received the request but could not refund it. Reason: ${reason}. This is not a timeout. Do not credit the agent wallet.`
        : "The airline received the request but could not refund it. This is not a timeout. Do not credit the agent wallet.",
      steps: [
        "Read the airline reason.",
        "Close this request if the ticket stays issued.",
        "Do not pay the agent.",
      ],
      tone: "danger",
    },
    refundcompleted: {
      statusLabel: "Completed",
      title: "Refund completed",
      description:
        "The airline refund succeeded and the agent wallet has been credited. No further action is needed.",
      steps: [],
      tone: "success",
    },
    success: {
      statusLabel: "Completed",
      title: "Refund completed",
      description:
        "The airline refund succeeded and the agent wallet has been credited. No further action is needed.",
      steps: [],
      tone: "success",
    },
    closed: {
      statusLabel: "Completed",
      title: "Refund completed",
      description:
        "The airline refund succeeded and settlement is done. No further action is needed.",
      steps: [],
      tone: "success",
    },
  };

  return overlays[code] || null;
}

export function isRefundNotApprovedError(message) {
  return /not been approved|approve the refund/i.test(String(message || ""));
}

export function getRefundPriceFailedCopy(res, { canQuote = false } = {}) {
  const notApproved = isRefundNotApprovedError(res?.msg);
  if (notApproved) {
    return {
      title: "Accept this request first",
      text: "Accept in Intraverse, then try Get supplier price again.",
      steps: [],
    };
  }
  return {
    title: "We could not get a price",
    text: "Okay — not an airline no. Do this next:",
    steps: canQuote
      ? [
          "On the airline site, look up the refund amount only — do not complete the refund.",
          'Come back and click "Enter quote from airline" with those numbers.',
        ]
      : [
          "Accept this request in Intraverse if you have not.",
          "On the airline site, look up the refund amount only — do not complete the refund.",
          'Come back and click "Enter quote from airline" with those numbers.',
        ],
  };
}

export function getRefundSupplierCheckCopy(res, currentStatus) {
  const code = Number(res?.code);
  const msg = String(res?.msg || "");
  const nextStatus = res?.data?.status || currentStatus;

  if (res?.return) {
    if (isSupplierProcessingStatus(nextStatus)) {
      return {
        phase: "wait",
        kind: "still_waiting",
        title: "Still waiting on the airline",
        text: "Okay — not done yet. Do not credit the wallet.",
        steps: [
          "You can open the airline site and check this ticket’s refund status yourself.",
          "If it is still processing, wait and try again here.",
          "If the airline already shows refunded, use Credit wallet.",
        ],
      };
    }
    if (nextStatus === "PendingRefund" || isRefundSuccessfulStatus(nextStatus) || isRefundSettlementPendingStatus(nextStatus)) {
      return {
        phase: "success",
        kind: "succeeded",
        title: "The airline refund succeeded",
        text: "Credit the agent wallet now.",
        steps: [],
      };
    }
    if (isRefundSettlementFailedStatus(nextStatus)) {
      return {
        phase: "error",
        kind: "reimburse_failed",
        title: "Wallet settlement failed",
        text: "Airline refund stays successful. Retry Credit wallet. Do not mark this refund failed.",
        steps: [],
      };
    }
    if (nextStatus === "Success" || nextStatus === "Closed" || isRefundCompletedStatus(nextStatus)) {
      return {
        phase: "success",
        kind: "completed",
        title: "This refund is complete",
        text: "Airline refund and wallet credit are done.",
        steps: [],
      };
    }
    if (["Failed", "Denied", "Rejected"].includes(nextStatus) || isRefundSupplierFailedStatus(nextStatus)) {
      return {
        phase: "error",
        kind: "supplier_failed",
        title: "The airline could not complete this refund",
        text: "This is a real airline outcome — not a timeout. Do not credit the wallet.",
        steps: [
          "You may open the airline site to confirm the decline/fail reason.",
          "Only use Credit wallet if you later confirm the airline did refund it.",
        ],
      };
    }
    return {
      phase: "success",
      kind: "updated",
      title: "Supplier status updated",
      text: "The latest status is on this request now.",
      steps: [],
    };
  }

  if (code === 711 || /no refund order record/i.test(msg)) {
    return {
      phase: "error",
      kind: "not_found",
      title: "No refund on file yet",
      text: "Okay — not an airline no. Do not credit the wallet.",
      steps: [
        "Open the airline site and check this ticket’s refund status yourself.",
        "If the refund is not submitted there yet, submit it on the airline site, then try again here.",
        "If the airline already shows refunded, use Credit wallet.",
      ],
    };
  }

  if (/network/i.test(msg)) {
    return {
      phase: "error",
      kind: "network",
      title: "We could not reach the supplier",
      text: `${msg}. Okay — not an airline no.`,
      steps: [
        "Open the airline site and check this ticket’s refund status yourself.",
        "Try again here in a bit.",
        "If the airline already shows refunded, use Credit wallet.",
      ],
    };
  }

  return {
    phase: "error",
    kind: "generic",
    title: "We could not check the airline yet",
    text: `${msg || "The request failed or timed out"}. Okay — not an airline no.`,
    steps: [
      "Open the airline site and check this ticket’s refund status yourself.",
      "Try again here in a bit.",
      "If the airline already shows refunded, use Credit wallet.",
    ],
  };
}

export function getRefundConfirmCopy(res) {
  const code = Number(res?.code);
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "confirmed",
      title: "Refund confirmed",
      text: "Confirmed on our side. Open the latest status next.",
      steps: [],
    };
  }
  if (code === 74 || /not allowed|request not accepted/i.test(msg)) {
    return {
      phase: "error",
      kind: "confirm_failed",
      title: "Confirm refund is not allowed here",
      text: "This request is probably past that step. If the airline already refunded it, use Credit wallet.",
      steps: [],
    };
  }
  return {
    phase: "error",
    kind: "confirm_failed",
    title: "We could not confirm this refund",
    text: `${msg || "The request failed or timed out"}. If the airline already refunded it, use Credit wallet.`,
    steps: [],
  };
}

export function getRefundConfirmWarningCopy() {
  return {
    phase: "warn",
    kind: "confirm_warn",
    title: "This is an advanced skip",
    text: "Confirm refund marks it confirmed here. It is not the usual way to pay the agent. Check the airline ticket first.",
    steps: [],
  };
}

export function getRefundReimburseWarningCopy(data) {
  const stage = getRefundAdminStage(data);
  if (isRefundWalletCreditStage(data)) {
    return {
      phase: "warn",
      kind: "reimburse_warn",
      title: "This credits the agent wallet",
      text: "Airline already refunded. This pays the agent. Cannot be reversed here.",
      steps: [],
    };
  }
  if (stage === "supplier_failed") {
    return {
      phase: "warn",
      kind: "reimburse_warn",
      title: "Only if the airline actually refunded this",
      text: "Airline recorded fail/decline. Credit wallet still pays the agent. Confirm on the airline site first.",
      steps: [],
    };
  }
  if (stage === "awaiting_supplier" || stage === "send_failed") {
    return {
      phase: "warn",
      kind: "reimburse_warn",
      title: "Only if the airline already shows refunded",
      text: "Use only if you checked the airline site and it is refunded, but this request is stuck. Cannot be reversed here.",
      steps: [],
    };
  }
  return {
    phase: "warn",
    kind: "reimburse_warn",
    title: "Only if the airline already shows refunded",
    text: "Credit wallet pays the agent and cannot be reversed here. Confirm on the airline site first.",
    steps: [],
  };
}

export function getRefundReimburseCopy(res) {
  const msg = String(res?.msg || "");
  if (res?.return) {
    return {
      phase: "success",
      kind: "reimbursed",
      title: "The agent wallet has been credited",
      text: "Open the latest status next.",
      steps: [],
    };
  }
  return {
    phase: "error",
    kind: "reimburse_failed",
    title: "We could not credit the wallet",
    text: `${msg || "The request failed or timed out"}. Try again, or check the airline site first.`,
    steps: [],
  };
}

export function needsRefundApproval(data) {
  if (
    !data ||
    data?.confirmed ||
    isRefundAcceptedStatus(data?.status)
  ) {
    return false;
  }
  const code = normalizeRefundStatus(data?.status);
  return (
    data?.status === "Pending" ||
    isManualReviewStatus(data?.status) ||
    code === "autorefundprocessing"
  );
}

export function getRefundAdminStage(data) {
  const status = data?.status;
  const quoted = hasRefundQuote(data);
  const confirmed = Boolean(data?.confirmed);
  const code = normalizeRefundStatus(status);
  // Soft expiry only while RefundQuoted — other statuses keep their normal stage
  const softExpired =
    isRefundQuotedStatus(status) &&
    quoted &&
    !confirmed &&
    isRefundQuoteExpired(data);

  if (
    ["Denied", "Rejected", "Cancelled", "Canceled"].includes(status) ||
    isRefundDeclinedByAgentStatus(status)
  ) {
    return "declined";
  }
  if (isRefundValidationFailedStatus(status)) return "validation_failed";
  if (isRefundSettlementFailedStatus(status)) return "settlement_failed";
  if (isRefundSupplierFailedStatus(status)) return "supplier_failed";
  if (isRefundCompletedStatus(status)) return "completed";
  if (
    isRefundSuccessfulStatus(status) ||
    isRefundSettlementPendingStatus(status) ||
    status === "PendingRefund"
  ) {
    return "needs_reimburse";
  }
  if (isSupplierProcessingStatus(status)) return "awaiting_supplier";
  if (isRefundSendFailedStatus(status)) return "send_failed";
  // AutoRefundProcessing: treat like ManualReviewRequired so Ops can review / manual path
  if (code === "autorefundprocessing") return "needs_review";
  if (isAutoRefundProcessingStatus(status)) return "auto_processing";
  if (isRefundAcceptedByAgentStatus(status)) return "needs_airline_submit";
  if (isRefundQuoteExpiredStatus(status)) return "quote_expired";
  if (isRefundQuotedStatus(status)) {
    if (confirmed) return "needs_airline_submit";
    if (softExpired) return "quote_expired";
    return "awaiting_agent";
  }
  if (isManualReviewStatus(status)) {
    // After agent accept / send fail: submit path, not quote path
    if (confirmed || isRefundAcceptedByAgentStatus(status)) {
      return "send_failed";
    }
    if (quoted) return "awaiting_agent";
    // AUTO_REFUND_FAILED → Ops must quote (already past the Accept gate for pricing)
    if (code === "autorefundfailed") {
      return "needs_quote";
    }
    // ManualReviewRequired: Accept / Decline only — quote after Accept
    return "needs_review";
  }
  if (status === "Pending") return "needs_review";
  if (isRefundAcceptedStatus(status)) {
    if (confirmed) return "needs_airline_submit";
    if (quoted) return "awaiting_agent";
    return "needs_quote";
  }
  return "unknown";
}

export function getRefundAdminGuidance(data) {
  const stage = getRefundAdminStage(data);
  const reason = data?.rejectReason || data?.reason || "";
  const assignLabel = getRefundAssignActionLabel(data);
  const declineLabel = getRefundDeclineActionLabel(data);
  const quoteLabel = getRefundQuoteActionLabel(data);
  const submitLabel = getRefundSubmitActionLabel();
  const reimburseLabel = getRefundReimburseActionLabel(data);

  const guidance = {
    needs_review: {
      title: "Ops must review this refund",
      description:
        "An agent submitted a refund. Do not submit on the airline site yet. Check the ticket, then Accept or Decline. After Accept you create a quote.",
      statusLabel: "Ops must review this refund",
      tone: "action",
      steps: [
        "Open Review.",
        "Check ticket, passengers, and reason.",
        "Open the airline site only to CHECK eligibility — do not submit a refund yet.",
        "Accept or Decline.",
      ],
      primary: [
        { key: REFUND_ACTION.review, label: "Review this request" },
        ...(canRetryAutoRefund(data)
          ? [
              {
                key: REFUND_ACTION.retryAuto,
                label: getRefundRetryAutoActionLabel(),
              },
            ]
          : []),
        { key: REFUND_ACTION.assign, label: assignLabel },
      ],
      secondary: [],
    },
    needs_manual_review: {
      title: "Ops must review this refund",
      description:
        "A person must look at this. Do not submit a refund on the airline site yet. Check the ticket, then Accept or Decline. After Accept you can get a supplier price or enter a quote.",
      statusLabel: "Ops must review this refund",
      tone: "action",
      steps: [
        "Open Review.",
        "Check ticket, passengers, and reason.",
        "Open the airline site only to CHECK eligibility — do not submit a refund yet.",
        "Accept or Decline.",
        "Only after Accept: Get supplier price or Enter quote from airline.",
      ],
      primary: [
        { key: REFUND_ACTION.review, label: "Review this request" },
        ...(canRetryAutoRefund(data)
          ? [
              {
                key: REFUND_ACTION.retryAuto,
                label: getRefundRetryAutoActionLabel(),
              },
            ]
          : []),
        { key: REFUND_ACTION.assign, label: assignLabel },
      ],
      secondary: [],
    },
    auto_processing: {
      title: "Getting the refund price from the supplier",
      description:
        "The computer is asking the supplier for a refund price. Do not submit a refund on the airline site yet. If this sits too long, take over: check the airline yourself, then enter a quote. A timeout is not an airline decline.",
      statusLabel: "Getting supplier price",
      tone: "wait",
      steps: [
        "Wait a short time for the supplier price.",
        "If stuck: open the airline site, get the refund numbers, then enter a quote.",
        "Do not submit the refund to the airline yet.",
      ],
      primary: [],
      secondary: [{ key: REFUND_ACTION.assign, label: assignLabel }],
    },
    needs_quote: {
      title: "Your job now: get the airline refund price, then enter the quote",
      description:
        "Do not invent the amount. Check how much the airline will refund, then enter that in Intraverse. Do not submit the refund on the airline site yet. Do not pay the agent.",
      statusLabel: "Get price — then enter quote",
      tone: "action",
      steps: [
        "Open the airline site and get the refund numbers (do not process the refund yet).",
        'Retry "Get supplier price", or "Enter quote from airline" with those numbers.',
        "Send the quote to the agent.",
        "Wait for the agent to accept — only then submit the refund on the airline site.",
      ],
      primary: [
        { key: REFUND_ACTION.autoQuote, label: "Get supplier price" },
        { key: REFUND_ACTION.quote, label: quoteLabel },
      ],
      secondary: [
        { key: REFUND_ACTION.assign, label: assignLabel },
        { key: REFUND_ACTION.decline, label: declineLabel },
      ],
    },
    awaiting_agent: data?.immediateRefund
      ? {
          title: "Immediate refund — approve the quote for the agent",
          description:
            "The agent chose immediate refund. Review the quote numbers carefully, then Approve quote. That accepts on their behalf. Do not credit the wallet yet.",
          statusLabel: "Approve quote (immediate refund)",
          tone: "action",
          steps: [
            "Open Approve quote and check breakdown, agent payable, and your profit.",
            "Approve only if the figures are correct — nobody else will catch a mistake.",
            "Then submit the refund on the airline site. Do not credit the wallet yet.",
          ],
          primary: [
            {
              key: REFUND_ACTION.approveQuote,
              label: getRefundApproveQuoteActionLabel(),
            },
          ],
          secondary: [
            { key: REFUND_ACTION.quote, label: quoteLabel },
            { key: REFUND_ACTION.assign, label: assignLabel },
            { key: REFUND_ACTION.decline, label: declineLabel },
          ],
        }
      : {
          title: "Waiting for the agent to accept the quote",
          description:
            "The quote is with the agent. Do not submit the refund to the airline yet. Do not credit the agent wallet.",
          statusLabel: "Waiting for agent to accept quote",
          tone: "wait",
          steps: [
            "Wait for the agent to accept or decline.",
            "Edit the quote only if figures need to change.",
            "Do not submit on the airline site yet.",
          ],
          primary: [{ key: REFUND_ACTION.quote, label: quoteLabel }],
          secondary: [
            { key: REFUND_ACTION.assign, label: assignLabel },
            { key: REFUND_ACTION.decline, label: declineLabel },
          ],
        },
    quote_expired: {
      title: "Quote expired — enter a new one",
      description:
        "The agent did not confirm in time. Enter a new quote. Do not submit this to the airline on the old quote. Do not credit the wallet.",
      statusLabel: "Quote expired — quote again",
      tone: "danger",
      steps: [
        "Enter a new quote with a fresh expiry.",
        "Do not submit this refund to the airline yet.",
        "Do not credit the agent wallet.",
      ],
      primary: [{ key: REFUND_ACTION.quote, label: quoteLabel }],
      secondary: [
        { key: REFUND_ACTION.assign, label: assignLabel },
        { key: REFUND_ACTION.decline, label: declineLabel },
      ],
    },
    needs_airline_submit: {
      title: "Submit this refund on the airline site now",
      description:
        "The agent accepted the quote. Intraverse has not refunded the ticket. Open the airline site, submit the refund there, then come back and tell Intraverse you submitted it. That does not pay the agent yet.",
      statusLabel: "Submit refund on airline site",
      tone: "action",
      steps: REFUND_AIRLINE_SUBMIT_STEPS,
      primary: [
        { key: REFUND_ACTION.submit, label: submitLabel },
        { key: REFUND_ACTION.reimburse, label: reimburseLabel },
      ],
      secondary: [
        { key: REFUND_ACTION.assign, label: assignLabel },
        { key: REFUND_ACTION.decline, label: declineLabel },
      ],
    },
    send_failed: {
      title: "Automatic send failed. Submit on the airline site",
      description:
        "Sending the refund failed for a technical reason. That is not an airline decline. The ticket is not refunded yet. Submit the refund yourself on the airline site.",
      statusLabel: "Send failed — submit on airline site",
      tone: "action",
      steps: REFUND_AIRLINE_SUBMIT_STEPS,
      primary: [
        { key: REFUND_ACTION.submit, label: submitLabel },
        { key: REFUND_ACTION.checkStatus, label: "Check supplier status" },
        { key: REFUND_ACTION.reimburse, label: reimburseLabel },
      ],
      secondary: [
        { key: REFUND_ACTION.assign, label: assignLabel },
        { key: REFUND_ACTION.decline, label: declineLabel },
      ],
    },
    awaiting_supplier: {
      title: "Waiting on the airline — do not submit again",
      description:
        "The refund was already submitted. Do not submit again. Check status if needed. Do not credit the agent wallet until the airline refund has succeeded.",
      statusLabel: "Waiting on airline",
      tone: "wait",
      steps: REFUND_AIRLINE_CHECK_ONLY_STEPS,
      primary: [
        { key: REFUND_ACTION.checkStatus, label: "Check supplier status" },
      ],
      secondary: [
        { key: REFUND_ACTION.reimburse, label: reimburseLabel },
        { key: REFUND_ACTION.assign, label: assignLabel },
        { key: REFUND_ACTION.decline, label: declineLabel },
      ],
    },
    needs_reimburse: {
      title: "Airline refunded the ticket. Credit the agent wallet",
      description:
        "The airline refund succeeded. Credit the agent's wallet with the quoted net refund. If wallet settlement fails, the airline refund stays successful — fix the money separately.",
      statusLabel: "Airline refunded — credit wallet",
      tone: "action",
      steps: [
        "Do not submit another refund on the airline site.",
        "Credit the agent wallet.",
        "If money fails, keep the airline refund as successful.",
      ],
      primary: [
        { key: REFUND_ACTION.reimburse, label: "Credit agent wallet" },
      ],
      secondary: [
        { key: REFUND_ACTION.checkStatus, label: "Check supplier status" },
        { key: REFUND_ACTION.assign, label: assignLabel },
      ],
    },
    settlement_failed: {
      title: "Wallet settlement failed",
      description:
        "The airline refund stays successful. Retry crediting the agent wallet. Do not change a successful refund back to failed.",
      statusLabel: "Airline done — money failed",
      tone: "danger",
      steps: [
        "Do not change the airline outcome.",
        "Retry Credit agent wallet.",
      ],
      primary: [
        { key: REFUND_ACTION.reimburse, label: "Credit agent wallet" },
      ],
      secondary: [
        { key: REFUND_ACTION.checkStatus, label: "Check supplier status" },
        { key: REFUND_ACTION.assign, label: assignLabel },
      ],
    },
    completed: {
      title: "Refund completed",
      description:
        "The airline refund succeeded and the agent wallet has been credited. No further action is needed.",
      statusLabel: "Completed",
      tone: "success",
      steps: [],
      primary: [],
      secondary: [],
    },
    declined: {
      title: "Request declined",
      description: reason
        ? `This refund was declined. Reason: ${reason}`
        : "This refund was declined. No wallet credit should be made.",
      statusLabel: "Declined",
      tone: "danger",
      steps: [],
      primary: [],
      secondary: [],
    },
    supplier_failed: {
      title: "The airline did not refund this ticket",
      description:
        "The airline declined or failed this refund. This is not a technical timeout. Close this request if the ticket stays issued. Do not credit the agent wallet unless you later confirm the airline did refund it.",
      statusLabel: "Airline declined refund",
      tone: "danger",
      steps: [
        "Read the airline reason.",
        "Close this request if the ticket stays issued.",
        "Do not pay the agent.",
      ],
      primary: [{ key: REFUND_ACTION.decline, label: declineLabel }],
      secondary: [
        { key: REFUND_ACTION.checkStatus, label: "Check supplier status" },
        { key: REFUND_ACTION.reimburse, label: reimburseLabel },
        { key: REFUND_ACTION.assign, label: assignLabel },
      ],
    },
    validation_failed: {
      title: "This refund could not be started",
      description:
        "Validation failed before anything was sent to the airline. No money moved. Close this request, then the agent can submit again if it is still eligible.",
      statusLabel: "Could not start",
      tone: "danger",
      steps: [
        "Read the validation reason.",
        "Close this request.",
        "Do not submit a refund on the airline site for this failed request.",
      ],
      primary: [{ key: REFUND_ACTION.decline, label: declineLabel }],
      secondary: [{ key: REFUND_ACTION.assign, label: assignLabel }],
    },
    unknown: {
      title: "Refund request",
      description:
        "Open the available actions below. If you are unsure, assign this to a teammate rather than guessing.",
      statusLabel: data?.status || "Unknown",
      tone: "info",
      steps: [],
      primary: [{ key: REFUND_ACTION.assign, label: assignLabel }],
      secondary: [],
    },
  };

  const result = {
    stage,
    ...(guidance[stage] || guidance.unknown),
  };
  const overlay = getRefundStatusOverlay(data, { stage });
  if (overlay) {
    if (overlay.title) result.title = overlay.title;
    if (overlay.description) result.description = overlay.description;
    if (overlay.statusLabel) result.statusLabel = overlay.statusLabel;
    if (overlay.tone) result.tone = overlay.tone;
    if (overlay.steps) result.steps = overlay.steps;
  }
  return result;
}

export function canCheckRefundMessages(data) {
  if (!data) return false;
  return Boolean(data?._id);
}

export function getRefundMessageCount(data) {
  const raw = data?.conversation;
  if (!Array.isArray(raw)) return 0;
  return raw.filter(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      Object.prototype.hasOwnProperty.call(entry, "message"),
  ).length;
}

export function getRefundOverflowMenuActions(data) {
  const stage = getRefundAdminStage(data);
  const guidance = getRefundAdminGuidance(data);
  const items = [];

  function push(action) {
    if (!action?.key || items.some((item) => item.key === action.key)) return;
    items.push(action);
  }

  // ManualReviewRequired / AutoRefundProcessing: slim list — Accept/Decline
  // are inside Review. View Request is added by FlightMenu above these items.
  if (stage === "needs_review") {
    if (needsRefundApproval(data)) {
      push({ key: REFUND_ACTION.review, label: "Review this request" });
    }
    if (canRetryAutoRefund(data)) {
      push({
        key: REFUND_ACTION.retryAuto,
        label: getRefundRetryAutoActionLabel(),
      });
    }
    if (canCheckRefundMessages(data)) {
      push({
        key: REFUND_ACTION.messages,
        label: "Check messages",
      });
    }
    if (canAssignRefundRequest(data)) {
      push({
        key: REFUND_ACTION.assign,
        label: getRefundAssignActionLabel(data),
      });
    }
    if (hasRefundSupplierResponse(data)) {
      push({
        key: REFUND_ACTION.supplierResponse,
        label: "Supplier Response",
      });
    }
    return items;
  }

  // Always offer messaging so Ops can start the thread (no count in the label).
  if (canCheckRefundMessages(data)) {
    push({
      key: REFUND_ACTION.messages,
      label: "Check messages",
    });
  }

  if (hasRefundSupplierResponse(data)) {
    push({
      key: REFUND_ACTION.supplierResponse,
      label: "Supplier Response",
    });
  }

  [...(guidance.primary || []), ...(guidance.secondary || [])].forEach(push);

  if (needsRefundApproval(data)) {
    push({ key: REFUND_ACTION.review, label: "Review this request" });
  }
  if (canRetryAutoRefund(data)) {
    push({
      key: REFUND_ACTION.retryAuto,
      label: getRefundRetryAutoActionLabel(),
    });
  }
  if (canGetAirlinePrice(data)) {
    push({ key: REFUND_ACTION.autoQuote, label: "Get supplier price" });
  }
  if (canEnterRefundQuote(data)) {
    push({
      key: REFUND_ACTION.quote,
      label: getRefundQuoteActionLabel(data),
    });
  }
  if (canApproveImmediateRefundQuote(data)) {
    push({
      key: REFUND_ACTION.approveQuote,
      label: getRefundApproveQuoteActionLabel(),
    });
  }
  if (canSubmitRefundToAirline(data)) {
    push({
      key: REFUND_ACTION.submit,
      label: getRefundSubmitActionLabel(),
    });
  }
  if (canCheckRefundSupplierStatus(data)) {
    push({
      key: REFUND_ACTION.checkStatus,
      label: "Check supplier status",
    });
  }
  if (canCreditRefundWallet(data)) {
    push({
      key: REFUND_ACTION.reimburse,
      label: getRefundReimburseActionLabel(data),
    });
  }
  if (canDeclineRefundRequest(data)) {
    push({
      key: REFUND_ACTION.decline,
      label: getRefundDeclineActionLabel(data),
    });
  }
  if (canAssignRefundRequest(data)) {
    push({
      key: REFUND_ACTION.assign,
      label: getRefundAssignActionLabel(data),
    });
  }
  if (canAddRefundRemark(data)) {
    push({ key: REFUND_ACTION.remark, label: "Add remark" });
  }

  return items;
}
